import { memo, useRef, useMemo, useCallback, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Crud, CrudMode, notification, rejectionError, transformCrudFormErrors, getSelectsOptions } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, SelectsOptions, ValidationError } from '@ohpen/enso-ui';

import { crudToActionMap, changeRequestButtonMessage, changeRequestNotificationMessage } from '@pmp/utils';
import { useFeatureToggle } from '@pmp/hooks';

import { getLenderProcurationFeesFormSchema } from '../formConfig';
import type { ProcurationFeeAssignmentsCrudFormProps, GenericChangeResponse } from '../interfaces';

const ProcurationFeeAssignmentsCrudFormBase = memo(
  <T, U extends GenericChangeResponse<any>>({
    mode,
    valueLists,
    initialValues,
    refreshTable,
    onClose,
    submitChangeRequest,
  }: ProcurationFeeAssignmentsCrudFormProps<T, U>): ReactElement | null => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(true);
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);

    const onConfirm: (formData: T) => Promise<ValidationError[] | void> = useCallback(
      values => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

        return submitChangeRequest(values, mode).then(({ data: { isValid, errors } }: { data: U }) => {
          if (!isValid) {
            const form = ref.current?.form;
            if (form) {
              const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
              if (errorFields.length) form.setFields(errorFields);
            }

            return transformCrudFormErrors(errors);
          }
          if (mode === CrudMode.EDIT) refreshTable();
          notification().success({
            message: changeRequestNotificationMessage(t, enableFourEyes),
          });
        });
      },
      [mode, t, refreshTable, submitChangeRequest, enableFourEyes],
    );

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('lender.lender.labels.defaultProcurationFee') }),
        [CrudMode.CREATE]: t('global.labels.createX', { x: t('lender.lender.labels.defaultProcurationFee') }),
        [CrudMode.EDIT]: t('global.labels.editX', { x: t('lender.lender.labels.defaultProcurationFee') }),
      };
      return titles[mode];
    }, [mode, t]);

    const crudProps: CrudProps<T> = useMemo(() => {
      const selectsOptions: SelectsOptions = (valueLists && getSelectsOptions(valueLists)) || {};

      return {
        ref,
        open,
        schema: getLenderProcurationFeesFormSchema(selectsOptions, mode),
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        initialValues,
        mode,
        onClose: handleOnClose,
        onConfirm,
      };
    }, [valueLists, open, mode, title, t, initialValues, handleOnClose, onConfirm, enableFourEyes]);

    return <Crud {...crudProps} />;
  },
);

export const ProcurationFeeAssignmentsCrudForm = memo(ProcurationFeeAssignmentsCrudFormBase) as <T, U>(
  props: ProcurationFeeAssignmentsCrudFormProps<T, U>,
) => ReactElement | null;
